import { t } from "@lingui/macro";
import { useLinkProps } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtom, useAtomValue } from "jotai";
import { uniqueId } from "lodash";
import { useFragment } from "react-relay";

import { BookingCard } from "@whoosh/design-system/src/apps/PatronApp/components/BookingCard/BookingCard";

import { currentProfileAtom } from "../../../../../../../features/Club/atoms";
import { isMoveBooking_atom } from "../../../../MultiSlotBookingScreen/atoms";
import {
  BookingSlot_booking$key,
  BookingSlot_booking$data,
} from "./__generated__/BookingSlot_booking.graphql";

type BookingSlotProps = {
  bookingRef: BookingSlot_booking$key;
  slotId: string;
  currentSlotId?: string;
  isRequestSlot?: boolean;
};

export type Variant = "Yours" | "AvailableToJoin" | "Busy";

type Player = {
  name: string;
  id: string;
  roundId: string;
} | null;

type RoundEdgeType =
  | NonNullable<BookingSlot_booking$data["rounds"]>["edges"]
  | null
  | undefined;

type RoundNodeType = NonNullable<RoundEdgeType>[0];

export const BookingSlot = ({
  bookingRef,
  slotId,
  isRequestSlot = false,
}: BookingSlotProps) => {
  const booking = useFragment(BookingSlotFragmentString, bookingRef);
  const isLesson = booking.__typename === "LessonBooking";
  const bookingSummaryLinkProps = useLinkProps({
    screen: "BookingSummary",
    params: {
      slotOrBookingId: booking?.id ?? slotId,
    },
  });

  const [isMoveBooking] = useAtom(isMoveBooking_atom);

  const durationMins = booking.durationMins || 0;

  const currentProfile = useAtomValue(currentProfileAtom);
  const currentUserId = currentProfile?.memberId ?? null;
  const extractPlayersFromBooking = (): Player[] => {
    const edges: RoundEdgeType = booking?.rounds?.edges;

    if (!edges || !Array.isArray(edges)) {
      return [];
    }

    const calculateName = (r: RoundNodeType) => {
      if (r?.node?.__typename === "GuestRound") {
        return `(G) ${
          r?.node?.player?.firstName
            ? `${r?.node?.player?.firstName} ${r?.node?.player?.lastName}`
            : t`Guest`
        }`;
      } else {
        return r?.node?.player?.firstName
          ? `${r?.node?.player?.firstName} ${r?.node?.player?.lastName}`
          : t`Member`;
      }
    };

    return edges.map((r: RoundNodeType) => {
      return r?.node?.player?.id && r?.node?.id
        ? {
            name: calculateName(r),
            id: r?.node?.player?.id,
            roundId: r?.node?.id,
          }
        : null;
    });
  };
  const players = extractPlayersFromBooking();
  const currentUserInBooking = currentUserId
    ? players.some((p) => p?.id === currentUserId)
    : false;

  const notMyLesson = isLesson && !currentUserInBooking;

  const timeSlotCapacity = booking?.startSlot?.capacity || 4;
  const variant: Variant = currentUserInBooking
    ? "Yours"
    : players.length >= timeSlotCapacity || booking?.writeAccess === "NONE"
    ? "Busy"
    : "AvailableToJoin";
  const showCard = durationMins > 0 && players;
  const hidePlayers = isRequestSlot && !currentUserInBooking;
  return showCard ? (
    <BookingCard
      durationMins={durationMins}
      onPress={isMoveBooking ? () => {} : bookingSummaryLinkProps.onPress}
      isMoveBooking={isMoveBooking}
      variant={variant}
      players={
        hidePlayers
          ? []
          : notMyLesson
          ? [
              {
                name: "Lesson",
                id: uniqueId("fakeId"),
                roundId: uniqueId("fakeRoundId"),
              },
            ]
          : players
      }
    />
  ) : null;
};
export const BookingSlotFragmentString = graphql`
  fragment BookingSlot_booking on Booking {
    id
    __typename
    name
    ... on StandardBooking {
      durationMins
      startSlot {
        ... on TimeSlot {
          time
          capacity(bookingType: STANDARD)
          agenda {
            facility {
              club {
                supportsPublic
              }
            }
          }
        }
      }
    }
    ... on LessonBooking {
      durationMins
      startSlot {
        ... on TimeSlot {
          time
          capacity(bookingType: LESSON)
          agenda {
            facility {
              club {
                supportsPublic
              }
            }
          }
        }
      }
    }
    canUpdate
    writeAccess
    rounds {
      edges {
        node {
          id
          __typename
          player {
            id
            firstName
            lastName
          }
        }
      }
    }
  }
`;
